/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Creative Domain & Utility Icons
   Custom SVG icons for domain concepts (ports, node categories)
   + Lucide icons for standard chrome utilities only.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { PortType } from '../../domain/types';

// ── Re-export approved Lucide utility icons ──
export {
  ZoomIn,
  ZoomOut,
  Maximize2,
  Undo2,
  Redo2,
  Trash2,
  Play,
  Save,
  Download,
  Upload,
  X,
  ChevronRight,
  ChevronDown,
  Plus,
  Search,
  PanelRight,
  PanelLeft,
  Eye,
  Sliders,
  Sparkles,
  Layers,
  Settings,
  HelpCircle,
  FileCode,
  Image as ImageIcon,
} from 'lucide-react';

// ── Domain Creative Port Glyphs (Alterly Custom Icon Language) ──

export const PortTypeGlyph: React.FC<{ type: PortType; size?: number }> = ({
  type,
  size = 12,
}) => {
  switch (type) {
    case 'color':
      return (
        <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
          <circle cx="6" cy="6" r="4.5" fill="#e8b4f8" stroke="#ffffff" strokeWidth="1" />
        </svg>
      );
    case 'number':
      return (
        <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
          <rect x="2" y="2" width="8" height="8" rx="2" fill="#a9e1f7" stroke="#ffffff" strokeWidth="1" />
        </svg>
      );
    case 'image':
      return (
        <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
          <polygon points="6,1.5 10.5,9.5 1.5,9.5" fill="#8fe46c" stroke="#ffffff" strokeWidth="1" />
        </svg>
      );
    case 'mask':
      return (
        <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
          <rect x="2" y="2" width="8" height="8" rx="4" fill="#f8d84e" stroke="#ffffff" strokeWidth="1" />
        </svg>
      );
    case 'text':
      return (
        <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
          <path d="M 3 3 L 9 3 M 6 3 L 6 9.5" stroke="#f0f0ee" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'boolean':
      return (
        <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
          <polygon points="6,2 10,6 6,10 2,6" fill="#63d9db" stroke="#ffffff" strokeWidth="1" />
        </svg>
      );
    case 'any':
    default:
      return (
        <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
          <circle cx="6" cy="6" r="3.5" stroke="#9b9b97" strokeWidth="1.5" strokeDasharray="2 1" />
        </svg>
      );
  }
};

// ── Node Category Domain Glyphs ──

export const CategoryGlyph: React.FC<{ category: string; size?: number }> = ({
  category,
  size = 14,
}) => {
  switch (category) {
    case 'Input':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="5" stroke="#63d9db" strokeWidth="1.5" />
          <circle cx="8" cy="8" r="2" fill="#63d9db" />
        </svg>
      );
    case 'Math':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
          <path d="M 4 8 L 12 8 M 8 4 L 8 12" stroke="#a9e1f7" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'Color':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
          <circle cx="6" cy="6" r="3.5" fill="#e8b4f8" fillOpacity="0.8" />
          <circle cx="10" cy="7" r="3.5" fill="#63d9db" fillOpacity="0.8" />
          <circle cx="8" cy="11" r="3.5" fill="#b99af8" fillOpacity="0.8" />
        </svg>
      );
    case 'Filter':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
          <path d="M 3 4 L 13 4 L 9.5 8.5 L 9.5 13 L 6.5 11 L 6.5 8.5 Z" stroke="#8fe46c" strokeWidth="1.5" fill="none" strokeLinejoin="round" />
        </svg>
      );
    case 'Output':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
          <rect x="3" y="3" width="10" height="10" rx="2" stroke="#b99af8" strokeWidth="1.5" />
          <polygon points="6,5.5 11,8 6,10.5" fill="#b99af8" />
        </svg>
      );
    default:
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
          <rect x="4" y="4" width="8" height="8" rx="2" stroke="#9b9b97" strokeWidth="1.5" />
        </svg>
      );
  }
};

// ── Alterly Studio Branded Logo ──

export const AlterlyStudioLogo: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <rect width="32" height="32" rx="7" fill="#141417" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
    <path d="M 8 16 C 14 16, 18 10, 24 10" stroke="#b99af8" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M 8 16 C 14 16, 18 22, 24 22" stroke="#63d9db" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="8" cy="16" r="3" fill="#0a0a0a" stroke="#ffffff" strokeWidth="2" />
    <circle cx="24" cy="10" r="2.5" fill="#b99af8" />
    <circle cx="24" cy="22" r="2.5" fill="#63d9db" />
  </svg>
);
